import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Notice } from '@app/_models/notice.model';
import { NoticeService } from '@app/_services/notice.service';
import { AccessManagementService } from '@app/_services/access-management.service';

interface ManagedProperty {
  id: number;
  name: string;
}

@Component({
    selector: 'app-manage-notices',
    templateUrl: './manage-notices.component.html',
    styleUrls: ['./manage-notices.component.css'],
    standalone: false
})
export class ManageNoticesComponent implements OnInit {

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  constructor(private noticeService: NoticeService,
    private accessManagementService: AccessManagementService,
    private toastr: ToastrService) { }

  faPlus = faPlus;
  faPen = faPen;
  faTrash = faTrash;

  properties = signal<ManagedProperty[]>([]);
  selectedPropertyId = signal<number | null>(null);
  notices = signal<Notice[]>([]);

  loadingProperties = signal(false);
  loadingNotices = signal(false);
  saving = signal(false);

  showForm = signal(false);
  editingId = signal<number | null>(null);

  form = this.fb.group({
    shortDescription: ['', [Validators.required, Validators.maxLength(255)]],
    longDescription: [''],
    dateVisible: ['', Validators.required],
    unit_number: [null as number | null, [Validators.min(0), Validators.pattern(/^\d*$/)]]
  });

  ngOnInit(): void {
    this.loadProperties();
  }

  loadProperties(): void {
    this.loadingProperties.set(true);
    this.accessManagementService.getManageablePropertyRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          const unique = new Map<number, ManagedProperty>();
          (data ?? []).forEach((entry: any) => {
            const id = Number(entry?.id);
            const role = String(entry?.rolename ?? '').toUpperCase();
            if (Number.isFinite(id) && (role === 'ROLE_ADMIN' || role === 'ADMIN')) {
              unique.set(id, { id, name: String(entry?.name || `Property ${id}`) });
            }
          });

          const properties = [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
          this.properties.set(properties);
          this.loadingProperties.set(false);

          if (properties.length === 1) {
            this.selectProperty(properties[0].id);
          }
        },
        error: () => {
          this.loadingProperties.set(false);
          this.toastr.error('Failed to load properties.', 'Error');
        }
      });
  }

  selectProperty(propertyId: number | null): void {
    this.selectedPropertyId.set(propertyId);
    this.cancelEdit();
    this.notices.set([]);
    if (propertyId !== null) {
      this.loadNotices(propertyId);
    }
  }

  selectedPropertyName(): string {
    return this.properties().find(p => p.id === this.selectedPropertyId())?.name ?? '';
  }

  loadNotices(propertyId: number): void {
    this.loadingNotices.set(true);
    this.noticeService.getPropertyNotices(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.notices.set(data ?? []);
          this.loadingNotices.set(false);
        },
        error: () => {
          this.notices.set([]);
          this.loadingNotices.set(false);
          this.toastr.error('Failed to load notices.', 'Error');
        }
      });
  }

  newNotice(): void {
    this.editingId.set(null);
    this.form.reset({
      shortDescription: '',
      longDescription: '',
      dateVisible: this.toLocalInput(new Date()),
      unit_number: null
    });
    this.showForm.set(true);
  }

  editNotice(notice: Notice): void {
    this.editingId.set(notice.id ?? null);
    this.form.reset({
      shortDescription: notice.shortDescription ?? '',
      longDescription: notice.longDescription ?? '',
      dateVisible: notice.dateVisible ? this.toLocalInput(new Date(notice.dateVisible)) : '',
      unit_number: notice.unit_number || null
    });
    this.showForm.set(true);
  }

  cancelEdit(): void {
    this.showForm.set(false);
    this.editingId.set(null);
    this.form.reset();
  }

  save(): void {
    const propertyId = this.selectedPropertyId();
    if (this.form.invalid || propertyId === null) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const payload: Notice = {
      propertyId,
      unit_number: Number(value.unit_number) || 0,
      dateVisible: new Date(value.dateVisible!).toISOString(),
      shortDescription: value.shortDescription!.trim(),
      longDescription: value.longDescription?.trim() ?? ''
    };

    const id = this.editingId();
    const request = id === null
      ? this.noticeService.createNotice(payload)
      : this.noticeService.updateNotice(id, payload);

    this.saving.set(true);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.saving.set(false);
        this.toastr.success(res?.message ?? 'Notice saved.', 'Success');
        this.cancelEdit();
        this.loadNotices(propertyId);
      },
      error: err => {
        this.saving.set(false);
        this.toastr.error(typeof err === 'string' ? err : 'Failed to save notice.', 'Error');
      }
    });
  }

  deleteNotice(notice: Notice): void {
    const propertyId = this.selectedPropertyId();
    if (notice.id === undefined || propertyId === null) {
      return;
    }
    if (!confirm(`Delete notice "${notice.shortDescription ?? ''}"?`)) {
      return;
    }

    this.noticeService.deleteNotice(notice.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.toastr.success(res?.message ?? 'Notice deleted.', 'Success');
          if (this.editingId() === notice.id) {
            this.cancelEdit();
          }
          this.loadNotices(propertyId);
        },
        error: err => {
          this.toastr.error(typeof err === 'string' ? err : 'Failed to delete notice.', 'Error');
        }
      });
  }

  isScheduled(notice: Notice): boolean {
    return !!notice.dateVisible && new Date(notice.dateVisible).getTime() > Date.now();
  }

  // datetime-local inputs need local time formatted as yyyy-MM-ddTHH:mm.
  private toLocalInput(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
